import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { systemService } from '../../../services';
import type { NotificationSettings } from '../../../services';
import { useToast } from '../../../components/common/ToastContainer';
import { usePermissions } from '../../../hooks/usePermissions';

import { tenantSettingsService } from '../../../services';
import api from '../../../services/api';

interface NotificationSettingsTabProps {
  settings: NotificationSettings | null;
  onUpdate: (settings: NotificationSettings) => void;
  isSuperAdmin?: boolean;
  tenantId?: string;
}

const NotificationSettingsTab = ({ settings, onUpdate, isSuperAdmin = true, tenantId = '' }: NotificationSettingsTabProps) => {
  const toast = useToast();
  const { canUpdate } = usePermissions();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<NotificationSettings | null>(settings);
  const [templates, setTemplates] = useState<any[]>([]);

  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await api.get('/email-templates');
        setTemplates(res.data);
      } catch (err) {
        console.error('Failed to fetch templates', err);
      }
    };
    fetchTemplates();
  }, []);

  const handleSave = async () => {
    if (!formData) return;

    try {
      setSaving(true);
      if (isSuperAdmin) {
        const updated = await systemService.updateNotifications(formData);
        onUpdate(updated);
      } else {
        // TenantAdmin: save overrides to tenant_settings
        await tenantSettingsService.updateCategory(tenantId, 'notifications', {
          email: formData.email,
          notificationTypes: formData.notificationTypes,
        });
        onUpdate(formData);
      }
      toast.success('Settings Updated', 'Notification settings updated successfully');
    } catch (err: any) {
      toast.error('Update Failed', `Failed to update notification settings: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!formData) {
    return <div className="text-gray-500">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Email Settings */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Configuration</h2>
        <div className="space-y-4">
          <ToggleField
            label="Enable Email Notifications"
            checked={formData.email.enabled}
            onChange={(checked) => setFormData({
              ...formData,
              email: { ...formData.email, enabled: checked }
            })}
          />
          {formData.email.enabled && (
            <>
              {/* Provider selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                  {(['smtp', 'graph'] as const).map((p) => {
                    const active = (formData.email.provider || 'smtp') === p;
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          email: { ...formData.email, provider: p }
                        })}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${active ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      >
                        {p === 'smtp' ? 'SMTP' : 'Microsoft Graph'}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {(formData.email.provider || 'smtp') === 'graph'
                    ? 'Sends via Microsoft Graph using an Azure App Registration with Mail.Send permission on a shared mailbox.'
                    : 'Sends via authenticated SMTP. Use this for any standards-compliant relay.'}
                </p>
              </div>

              {(formData.email.provider || 'smtp') === 'smtp' ? (
                <>
                  <FormField
                    label="SMTP Server"
                    value={formData.email.smtpServer}
                    onChange={(e) => setFormData({
                      ...formData,
                      email: { ...formData.email, smtpServer: e.target.value }
                    })}
                  />
                  <FormField
                    label="SMTP Port"
                    value={formData.email.smtpPort.toString()}
                    onChange={(e) => setFormData({
                      ...formData,
                      email: { ...formData.email, smtpPort: parseInt(e.target.value) || 587 }
                    })}
                    type="number"
                  />
                  <FormField
                    label="SMTP Username"
                    value={formData.email.smtpUser}
                    onChange={(e) => setFormData({
                      ...formData,
                      email: { ...formData.email, smtpUser: e.target.value }
                    })}
                  />
                  <FormField
                    label="SMTP Password"
                    value={formData.email.smtpPassword}
                    onChange={(e) => setFormData({
                      ...formData,
                      email: { ...formData.email, smtpPassword: e.target.value }
                    })}
                    type="password"
                  />
                </>
              ) : (
                <>
                  <FormField
                    label="Azure Tenant ID"
                    value={formData.email.graph?.tenantId || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      email: {
                        ...formData.email,
                        graph: { ...(formData.email.graph || { tenantId: '', clientId: '', clientSecret: '', senderAddress: '' }), tenantId: e.target.value },
                      },
                    })}
                  />
                  <FormField
                    label="Client ID (App Registration)"
                    value={formData.email.graph?.clientId || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      email: {
                        ...formData.email,
                        graph: { ...(formData.email.graph || { tenantId: '', clientId: '', clientSecret: '', senderAddress: '' }), clientId: e.target.value },
                      },
                    })}
                  />
                  <FormField
                    label="Client Secret"
                    value={formData.email.graph?.clientSecret || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      email: {
                        ...formData.email,
                        graph: { ...(formData.email.graph || { tenantId: '', clientId: '', clientSecret: '', senderAddress: '' }), clientSecret: e.target.value },
                      },
                    })}
                    type="password"
                  />
                  <FormField
                    label="Sender Mailbox (Mail.Send target)"
                    value={formData.email.graph?.senderAddress || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      email: {
                        ...formData.email,
                        graph: { ...(formData.email.graph || { tenantId: '', clientId: '', clientSecret: '', senderAddress: '' }), senderAddress: e.target.value },
                      },
                    })}
                  />
                  <p className="text-xs text-gray-500">
                    The sender mailbox must match the address the App Registration has Mail.Send permission on (e.g. <code>no-reply@yourdomain.com</code>).
                  </p>
                </>
              )}

              <FormField
                label="From Address"
                value={formData.email.fromAddress}
                onChange={(e) => setFormData({
                  ...formData,
                  email: { ...formData.email, fromAddress: e.target.value }
                })}
              />
              <FormField
                label="From Name"
                value={formData.email.fromName}
                onChange={(e) => setFormData({
                  ...formData,
                  email: { ...formData.email, fromName: e.target.value }
                })}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Alert Template</label>
                <select
                  value={formData.email.alertTemplateId || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    email: { ...formData.email, alertTemplateId: e.target.value }
                  })}
                  className="input-base w-full"
                >
                  <option value="">Default System Alert</option>
                  {templates.filter(t => t.type === 'content').map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Notification Types */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Notification Types</h2>
        <div className="space-y-4">
          {Object.entries(formData.notificationTypes).map(([key, value]) => (
            <div key={key} className="p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 capitalize">
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </h3>
              <div className="space-y-2">
                <ToggleField
                  label="Email"
                  checked={value.email}
                  onChange={(checked) => setFormData({
                    ...formData,
                    notificationTypes: {
                      ...formData.notificationTypes,
                      [key]: { ...value, email: checked }
                    }
                  })}
                />
                <ToggleField
                  label="SMS"
                  checked={false}
                  onChange={() => {}}
                  disabled
                />
                <ToggleField
                  label="Webhook"
                  checked={false}
                  onChange={() => {}}
                  disabled
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      {canUpdate && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

const FormField = ({
  label,
  value,
  onChange,
  type = 'text'
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: 'text' | 'number' | 'password';
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      className="input-base w-full"
    />
  </div>
);

const ToggleField = ({
  label,
  checked,
  onChange,
  disabled
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) => (
  <div className={`flex items-center justify-between ${disabled ? 'opacity-50' : ''}`}>
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <label className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
        disabled={disabled}
      />
      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
    </label>
  </div>
);

export default NotificationSettingsTab;

